/* eslint-disable @typescript-eslint/no-explicit-any */
import momentWrapper from '@utils/moment-wrapper';
import isString from 'lodash/isString';
import { VanillaTimeSeries } from './vanilla-time-series';
import { common as c } from '@pendo/aggregations';
import moment from 'moment';

interface DateRangeConfig {
    type?: string;
    start?: number | string;
    end?: number | string;
    count?: number;
    relativeTo?: DateRangeConfig;
}

export class VanillaDateRange {
    type: string;

    start?: number | string;

    end?: number | string;

    count?: number;

    relativeTo?: DateRangeConfig;

    constructor (config: DateRangeConfig = {}) {
        if (!config) {
            config = {};
        }
        this.type = config.type || 'Custom';
        this.start = config.start;
        this.end = config.end;
        this.count = config.count;
        this.relativeTo = config.relativeTo;
    }

    /**
     * Convert the date range to a time series.
     */
    timeSeries (today?: any, period: c.TimeSeriesPeriodType = 'dayRange'): VanillaTimeSeries {
        const timeSeriesConfig: c.TimeSeries = {
            period: period as c.TimeSeriesPeriodType,
        };

        today = momentWrapper(today).startOf('day');

        if (this.type === 'Today') {
            timeSeriesConfig.first = today.valueOf();
            timeSeriesConfig.count = 1;
        } else if (this.type === 'Yesterday') {
            timeSeriesConfig.first = today.subtract(1, 'days').valueOf();
            timeSeriesConfig.count = 1;
        } else if (this.type === 'Custom') {
            let { start } = this;
            let { end } = this;
            let { count } = this;

            if (start && end && start > end) {
                const tmp = end;
                end = start;
                start = tmp;
            }

            if (isString(start)) {
                start = momentWrapper(start).valueOf();
            }

            if (isString(end)) {
                end = momentWrapper(end).valueOf();
            }

            timeSeriesConfig.first = typeof start === 'string' ? momentWrapper(start).valueOf() : start;

            if (start && end) {
                count = Math.round(momentWrapper(end).diff(momentWrapper(start), 'days', true));
            }
            timeSeriesConfig.count = count || 1;
        } else if (this.type === 'Previous Period') {
            const relativeTimeSeries = new VanillaDateRange(this.relativeTo!).timeSeries();

            timeSeriesConfig.first = momentWrapper(relativeTimeSeries.first)
                .subtract(relativeTimeSeries.count, 'day')
                .valueOf();
            timeSeriesConfig.count = relativeTimeSeries.count;
        } else if (this.type === 'Previous Year') {
            const relativeTimeSeries = new VanillaDateRange(this.relativeTo!).timeSeries();

            timeSeriesConfig.first = momentWrapper(relativeTimeSeries.first)
                .subtract(1, 'year')
                .valueOf();
            timeSeriesConfig.count = relativeTimeSeries.count;
        }

        const match = /(Last\s+)?(\d+)\s+(Hours|Days|Weeks|Months)/.exec(this.type);
        if (match) {
            const offset = (match[1] || '').trim().toLowerCase();
            const count = parseInt(match[2], 10);
            const granularity = match[3].toLowerCase();
            const end = momentWrapper(today)
                .add(1, granularity as moment.unitOfTime.DurationConstructor)
                .startOf(granularity as moment.unitOfTime.StartOf);

            if (offset === 'last') {
                end.subtract(1, granularity as moment.unitOfTime.DurationConstructor);
            }

            const start = end.clone().subtract(count, granularity as moment.unitOfTime.DurationConstructor);
            timeSeriesConfig.first = start.valueOf();
            timeSeriesConfig.count = end.diff(start, 'days');
        }

        return new VanillaTimeSeries(timeSeriesConfig);
    }

    relativeTimeSeries (period: c.TimeSeriesPeriodType = 'dayRange'): c.TimeSeries | VanillaTimeSeries {
        const timeSeries: c.TimeSeries = {
            period: period as c.TimeSeriesPeriodType,
        };

        if (this.type === 'Today') {
            if (period === 'hourRange' || period === 'hourly') {
                timeSeries.first = 'startOfPeriod("daily",now())';
                timeSeries.count = 24;
            } else {
                timeSeries.first = 'now()';
                timeSeries.last = 'now()';
            }
        } else if (this.type === 'Yesterday') {
            if (period === 'hourRange' || period === 'hourly') {
                timeSeries.first = 'dateAdd(startOfPeriod("daily",now()),-1,"days")';
                timeSeries.count = 24;
            } else {
                timeSeries.first = 'dateAdd(startOfPeriod("daily",now()),-1,"days")';
                timeSeries.last = 'dateAdd(startOfPeriod("daily",now()),-1,"days")';
            }
        } else if (this.type === 'Custom') {
            return this.timeSeries(undefined, period);
        }

        const match = /(Last\s+)?(\d+)\s+(Hours|Days|Weeks|Months)/.exec(this.type);
        if (match) {
            const offset = (match[1] || '').trim().toLowerCase();
            const count = parseInt(match[2], 10);
            const granularity = match[3].toLowerCase();
            timeSeries.first = `dateAdd(startOfPeriod("daily",now()),-${count},"${granularity}")`;

            if (offset === 'last') {
                if (granularity === 'days') {
                    switch (period) {
                        case 'weekRange':
                        case 'weekly':
                            timeSeries.count = Math.ceil(count / 7);
                            break;
                        case 'monthRange':
                        case 'monthly':
                            timeSeries.count = Math.ceil(count / 30);
                            break;
                        default:
                            timeSeries.last = 'dateAdd(startOfPeriod("daily",now()),-1,"days")';
                    }
                } else {
                    // Ignore period if non-day granularity provided
                    timeSeries.last = 'dateAdd(startOfPeriod("daily",now()),-1,"days")';
                }
            } else {
                timeSeries.last = 'now()';
            }
        }

        return timeSeries;
    }

    /**
     * Get a string representation of the date range.
     */
    toString ({ relative = true }: { relative?: boolean } = {}): string {
        if (this.type === 'Custom' || !relative) {
            return this.timeSeries().toString({ relative });
        }

        return this.type;
    }
}
